import { useEffect } from "react"
import type { MutableRefObject } from "react"
import type { QueryClient } from "@tanstack/react-query"
import type { AxiosResponse } from "axios"
import {
	reorderFurnitureInCache,
	spliceErefIntoCache,
	spliceFurnitureIntoCache,
	spliceEcallIntoCache,
	applyDisconnectedEcallsToCache,
	applyUpdatedErefsToCache,
	applyPageUpdateToCache,
	findEntityVersion,
	findPageInfoVersion,
} from "helpers/quotationCache.helper"
import { enqueueMutation, getQueueVersionRef } from "store/mutationQueue.store"
import { furnitureQueueKey } from "../FurnitureSidebar/furnitureQueueKey"
import { erefQueueKey } from "../ReferenceSidebar/erefQueueKey"
import { ecallQueueKey } from "../EcallSidebar/ecallQueueKey"
import { pageQueueKey } from "./pageQueueKey"
import AnnotationService from "services/annotations.service"
import type {
	TCoordinates,
	TUpdateEcallRequest,
	TUpdateEcallResponse,
	TUpdateErefRequest,
	TUpdateErefResponse,
	TUpdateFurnitureRequest,
	TUpdateFurnitureResponse,
	TUpdatePageRequest,
	TUpdatePageResponse,
} from "types/elevationReferences.type"
import type { TAnnotationMetadata } from "types/annotations.type"
import type { TQuotationDetail } from "types/quotations.type"
import type { TViewerMode } from "store/viewer.store"

// Debounce (ms) for persisting a highlight move/resize. Apryse can fire "annotationChanged"
// repeatedly across a single drag gesture; coalesce them so only the final position is PATCHed.
const COORD_COMMIT_DEBOUNCE_MS = 150

// REQ-350 highlight coordinate editing. All state lives in the orchestrator; this hook owns only
// the AnnotationService callback wiring and receives the stable refs/mutations it needs. Behavior
// is unchanged from the inline version — the dependency array lists the passed-in mutations/values
// (all referentially stable) explicitly.
export type CoordinateCommitContext = {
	mode: TViewerMode
	isInitialized: boolean
	queryClient: QueryClient
	formatError: (error: unknown) => void
	numericQuotationIdRef: MutableRefObject<number | undefined>
	quotationDetailRef: MutableRefObject<TQuotationDetail | undefined>
	isCoordinateEditAllowed: () => boolean
	updateFurnitureAsync: (args: {
		quotationId: number
		furnitureId: number
		body: TUpdateFurnitureRequest
	}) => Promise<AxiosResponse<TUpdateFurnitureResponse> | undefined>
	updateErefAsync: (args: {
		quotationId: number
		erefId: number
		body: TUpdateErefRequest
	}) => Promise<AxiosResponse<TUpdateErefResponse> | undefined>
	updateEcallAsync: (args: {
		quotationId: number
		ecallId: number
		body: TUpdateEcallRequest
	}) => Promise<AxiosResponse<TUpdateEcallResponse> | undefined>
	updatePageAsync: (args: {
		quotationId: number
		pageInfoId: number
		body: TUpdatePageRequest
	}) => Promise<AxiosResponse<TUpdatePageResponse> | undefined>
}

// Persist highlight coordinate edits (drag-move / resize) to the backend (REQ-350). The
// annotation service recovers backend coords from the moved highlight and calls back here; we
// PATCH the entity with its current optimistic-lock version, splice the response into the
// quotation cache, and re-lock the highlight onto the authoritative coords. On any failure —
// backend error, empty body, or a pre-flight guard — we revert the highlight to its last-saved
// position so the canvas never shows an unsaved move; backend errors additionally surface the
// message (duplicate coords / version mismatch / validation) and refetch.
//
// Furniture and eref coordinate commits share the SAME per-entity mutation queue their sidebars
// use (both go through getQueueVersionRef/enqueueMutation on furnitureQueueKey/erefQueueKey), so
// a coordinate edit can't race a concurrent dimensions/status edit on the same entity. ECALL and
// PAGE are the exception — ecall tracks its version with a component-local ref, and a page's only
// other edit (the "Set page name" modal) PATCHes …/pages/ directly, neither via the shared queue —
// so their coordinate commits are serialized only against other coordinate commits on the same
// entity, not against those sibling edits; a rare concurrent sibling edit + drag can still lose the
// optimistic-lock race, which self-heals via the error path (toast + refetch + revert).
export const useCoordinateCommit = (ctx: CoordinateCommitContext) => {
	const {
		mode,
		isInitialized,
		queryClient,
		formatError,
		numericQuotationIdRef,
		quotationDetailRef,
		isCoordinateEditAllowed,
		updateFurnitureAsync,
		updateErefAsync,
		updateEcallAsync,
		updatePageAsync,
	} = ctx

	useEffect(() => {
		if (mode !== "editor" || !isInitialized) return

		// Keyed by `${entityType}-${entityId}`. Holds the debounce timer plus a `run` thunk that
		// fires the commit immediately — so effect teardown can FLUSH a still-pending move instead
		// of dropping it (which would silently lose a correction made just before a mode switch or
		// close). `done` guards against a double-commit if the timer and a flush both fire.
		const pending = new Map<string, { timer: ReturnType<typeof setTimeout>; run: () => void }>()

		const setQuotationCache = (
			qid: number,
			updater: (cached: AxiosResponse<TQuotationDetail> | undefined) => AxiosResponse<TQuotationDetail> | undefined,
		) => queryClient.setQueryData<AxiosResponse<TQuotationDetail> | undefined>(["quotation", qid], updater)

		// Put the highlight back to its last-saved coords when a move can't be persisted, so the
		// canvas never advertises an unsaved edit as saved.
		const revert = (metadata: TAnnotationMetadata, previous: TCoordinates | undefined) => {
			if (previous) AnnotationService.applyEntityCoordinates(metadata.entityType, metadata.entityId, previous)
		}

		const commit = (metadata: TAnnotationMetadata, coords: TCoordinates, previous: TCoordinates | undefined) => {
			const qid = numericQuotationIdRef.current
			const { entityType, entityId } = metadata
			// Page versions live in page_infos[] (their own lookup); the other three live in
			// related_objects (findEntityVersion). Narrows entityType away from "page" in the else.
			const cachedVersion =
				entityType === "page"
					? findPageInfoVersion(quotationDetailRef.current, entityId)
					: findEntityVersion(quotationDetailRef.current, entityType, entityId)
			// Can't build a valid PATCH (no quotation id, or the entity isn't in the cache to read a
			// version from) — revert so the user isn't left thinking an un-persisted move stuck.
			if (qid == null || cachedVersion == null) {
				revert(metadata, previous)
				return
			}
			const key =
				entityType === "furniture"
					? furnitureQueueKey(entityId)
					: entityType === "eref"
						? erefQueueKey(entityId)
						: entityType === "ecall"
							? ecallQueueKey(entityId)
							: pageQueueKey(entityId)
			// Monotonic shared ref: floors at the cached version but keeps any higher value a
			// preceding edit already advanced it to.
			const versionRef = getQueueVersionRef(key, cachedVersion)

			void enqueueMutation(key, versionRef.current, async () => {
				try {
					if (entityType === "furniture") {
						const res = await updateFurnitureAsync({
							quotationId: qid,
							furnitureId: entityId,
							body: { version: versionRef.current, ...coords },
						})
						const data = res?.data as TUpdateFurnitureResponse | undefined
						const updated = data?.this_furniture
						if (!updated) {
							revert(metadata, previous)
							return
						}
						versionRef.current = updated.version
						setQuotationCache(qid, (cached) => {
							const next = spliceFurnitureIntoCache(cached, updated)
							return data?.ordering ? reorderFurnitureInCache(next, data.ordering) : next
						})
						AnnotationService.applyEntityCoordinates("furniture", entityId, updated.coordinates)
					} else if (entityType === "eref") {
						const res = await updateErefAsync({
							quotationId: qid,
							erefId: entityId,
							body: { version: versionRef.current, ...coords },
						})
						const updated = res?.data as TUpdateErefResponse | undefined
						if (!updated) {
							revert(metadata, previous)
							return
						}
						versionRef.current = updated.version
						setQuotationCache(qid, (cached) => {
							const next = spliceErefIntoCache(cached, updated)
							return applyDisconnectedEcallsToCache(
								next,
								updated.disconnected_ecalls,
								updated.disconnected_ecall_elevations,
							)
						})
						AnnotationService.applyEntityCoordinates("eref", entityId, updated.coordinates)
					} else if (entityType === "ecall") {
						const res = await updateEcallAsync({
							quotationId: qid,
							ecallId: entityId,
							body: { version: versionRef.current, ...coords },
						})
						const updated = res?.data as TUpdateEcallResponse | undefined
						if (!updated) {
							revert(metadata, previous)
							return
						}
						versionRef.current = updated.version
						setQuotationCache(qid, (cached) => {
							const next = spliceEcallIntoCache(cached, updated)
							return applyUpdatedErefsToCache(next, updated.disconnected_erefs)
						})
						AnnotationService.applyEntityCoordinates("ecall", entityId, updated.coordinates)
					} else {
						// Page detection bbox (…/pages/{page_info_id}/). Coordinates only — the name is
						// edited separately via the "Set page name" modal. The response mirrors the eref
						// edit (updated page_info + affected erefs + any disconnected ecalls), which
						// applyPageUpdateToCache splices in without a refetch.
						const res = await updatePageAsync({
							quotationId: qid,
							pageInfoId: entityId,
							body: { version: versionRef.current, coordinates: coords },
						})
						const data = res?.data as TUpdatePageResponse | undefined
						const updated = data?.page_info
						if (!updated) {
							revert(metadata, previous)
							return
						}
						versionRef.current = updated.version
						setQuotationCache(qid, (cached) => applyPageUpdateToCache(cached, data!))
						AnnotationService.applyEntityCoordinates("page", entityId, {
							x0: updated.x0,
							y0: updated.y0,
							x1: updated.x1,
							y1: updated.y1,
						})
					}
				} catch (e) {
					formatError(e)
					queryClient.invalidateQueries({ queryKey: ["quotation", qid] })
					revert(metadata, previous)
				}
			})
		}

		AnnotationService.onAnnotationCoordinatesChanged = (metadata, coords) => {
			if (!isCoordinateEditAllowed()) return
			const key = `${metadata.entityType}-${metadata.entityId}`
			// renderedRect still holds the last-saved coords at this point — capture them for the
			// error-revert path.
			const previous = metadata.renderedRect ? { ...metadata.renderedRect } : undefined
			const existing = pending.get(key)
			if (existing) clearTimeout(existing.timer)
			let done = false
			const run = () => {
				if (done) return
				done = true
				pending.delete(key)
				commit(metadata, coords, previous)
			}
			pending.set(key, { timer: setTimeout(run, COORD_COMMIT_DEBOUNCE_MS), run })
		}

		return () => {
			AnnotationService.onAnnotationCoordinatesChanged = undefined
			// Flush (not drop) any pending commit so a move made just before teardown still persists.
			for (const { timer, run } of pending.values()) {
				clearTimeout(timer)
				run()
			}
			pending.clear()
		}
		// numericQuotationIdRef / quotationDetailRef are stable refs (listed to satisfy
		// exhaustive-deps; their identity never changes, so they don't re-run the effect).
	}, [
		mode,
		isInitialized,
		queryClient,
		formatError,
		numericQuotationIdRef,
		quotationDetailRef,
		isCoordinateEditAllowed,
		updateFurnitureAsync,
		updateErefAsync,
		updateEcallAsync,
		updatePageAsync,
	])
}
